import { format } from 'date-fns';
import { useMyRecommendedOutreachMembers } from '@/hooks/members/useMyRecommendedOutreachMembers';
import type { MemberDataset } from '@/lib/member-views/memberDataset.types';
import { sortFnTags } from '@/lib/member-views/sortRows';
import type { RecommendedOutreachMemberRow } from '@/lib/members/members.types';
import { reportOnly as reportOnlyFor } from './reportOnly';

const reportOnly = reportOnlyFor<RecommendedOutreachMemberRow>;

/**
 * Recommended Outreach — the practice's Active members renewing within the next 90 days who look
 * likely to cancel, for contacting them before the renewal date.
 *
 * The report is legacy's 20 columns below, in `csv.order`. Renewal and Last AWP are the table's own
 * fields; the table's Patient and Phone combine two values each, so the report has its own name and
 * phone columns.
 */
export const recommendedOutreachMembersDataset: MemberDataset<RecommendedOutreachMemberRow> = {
  key: 'outreach',
  rowKey: (row) => row.id,
  useRows: useMyRecommendedOutreachMembers,
  alphaKey: (row) => row.lastName,
  // Soonest renewal first is what the view is for; the server returns last name A–Z.
  defaultSort: [{ id: 'renewal', desc: false }],
  // The same provisional ceiling as All Active; these members are a subset of it.
  loading: { mode: 'full', ceiling: 10_000 },
  // Local date, not `toISOString()`, which is already tomorrow on a US evening.
  csvFileName: ({ date }) => `My_Patient_Recommended_Outreach_${format(date, 'yyyy-MM-dd')}.csv`,
  fields: [
    {
      id: 'patient',
      label: 'memberViews.columns.patient',
      width: 'xl',
      // Sorts on "last first"; the cell shows the name the other way round, with the MDVIP ID.
      value: (row) => [row.lastName, row.firstName].filter(Boolean).join(' ') || null,
      table: true,
      sortable: true,
    },
    {
      id: 'renewal',
      label: 'memberViews.columns.renewal',
      width: 'sm',
      value: (row) => row.renewalDate,
      format: 'date',
      table: true,
      sortable: true,
      csv: { order: 6, label: 'memberViews.csv.columns.renewalDate' },
    },
    {
      id: 'lastAwp',
      label: 'memberViews.columns.lastAwp',
      width: 'sm',
      value: (row) => row.lastAwpDate,
      format: 'date',
      table: true,
      sortable: true,
      csv: { order: 10, label: 'memberViews.csv.columns.lastAwpDate' },
    },
    {
      id: 'phone',
      label: 'memberViews.columns.phone',
      width: 'lg',
      value: (row) => row.mobilePhone ?? row.phone,
      format: 'phone',
      table: true,
    },
    {
      id: 'priorityTags',
      label: 'memberViews.columns.priorityTags',
      width: 'xl',
      // No tags is a missing value, so untagged rows sort last both ways, as the design asks.
      value: (row) => (row.priorityTags.length > 0 ? row.priorityTags : null),
      table: true,
      sortable: true,
      sortFn: sortFnTags,
    },
    reportOnly('title', 1, 'memberViews.csv.columns.title', (row) => row.salutation),
    reportOnly('lastName', 2, 'memberViews.csv.columns.lastName', (row) => row.lastName),
    reportOnly('firstName', 3, 'memberViews.csv.columns.firstName', (row) => row.firstName),
    reportOnly('mdvipId', 4, 'memberViews.csv.columns.mdvipId', (row) => row.mdvipId),
    reportOnly('established', 5, 'memberViews.csv.columns.dateEstablished', (row) => row.memberSince, 'date'),
    // The phone numbers as the API returns them, as legacy's report printed them.
    reportOnly('homePhone', 7, 'memberViews.csv.columns.phone', (row) => row.phone),
    reportOnly('cellPhone', 8, 'memberViews.csv.columns.cellPhone', (row) => row.mobilePhone),
    reportOnly('email', 9, 'memberViews.csv.columns.email', (row) => row.email),
    reportOnly('status', 11, 'memberViews.csv.columns.status', (row) => row.memberStatus),
    reportOnly('dob', 12, 'memberViews.csv.columns.dob', (row) => row.dateOfBirth, 'date'),
    reportOnly(
      'paymentSchedule',
      13,
      'memberViews.csv.columns.paymentSchedule',
      (row) => row.currentAgreementPaymentSchedule,
    ),
    reportOnly('amountDue', 14, 'memberViews.csv.columns.amountDue', (row) => row.currentBalanceFormula, 'currency'),
    // Always 0: being current on payments is one of the server's rules for this view.
    reportOnly('daysPastDue', 15, 'memberViews.csv.columns.daysPastDue', (row) => row.billingDaysPastDue, 'number'),
    reportOnly('autoPay', 16, 'memberViews.csv.columns.autoPay', (row) => row.autoPay, 'yesBlank'),
    reportOnly('lastPaymentDate', 17, 'memberViews.csv.columns.lastPaymentDate', (row) => row.lastPaymentDate, 'date'),
    // Always blank, as in legacy's report: the server deliberately doesn't return it for this view.
    reportOnly(
      'lastPaymentAmount',
      18,
      'memberViews.csv.columns.lastPaymentAmount',
      (row) => row.lastPaymentAmount,
      'currency',
    ),
    // "No" survives here, unlike Auto Pay: legacy printed this field's own Yes/No.
    reportOnly(
      'billToCorporateAccount',
      19,
      'memberViews.csv.columns.billToCorporateAccount',
      (row) => row.billToNationalAccount,
      'yesNoBlank',
    ),
    reportOnly('corporateAccount', 20, 'memberViews.csv.columns.corporateAccount', (row) => row.nationalAccountName),
  ],
};
